import * as React from "react";
import { cn } from "../lib/cn";
import { nx, NX_RING_CLIP } from "../lib/onyx-material";
import { AikoMark, type AikoMarkProps } from "./AikoMark";

/** BrandTile — the brand anchor tile, now in the Onyx hex-notch silhouette.
 *
 * BrandTile is the one surface BrandTile renders itself: a steel-toned `nx-s`
 * (card-scale) notched square holding the `<AikoMark>` glyph. Because the
 * silhouette is a `clip-path`, the two v1 surface details are drawn the
 * clip-safe way (issue #158):
 *   - ELEVATION is the `.elev-card` `drop-shadow` (traces the notch), NOT the
 *     steel `box-shadow` — an outer box-shadow is sliced away by the clip-path.
 *   - The OUTLINE is a continuous `nx-rim` RING overlay (toned to `--border`),
 *     NOT a real CSS `border` — a border is sliced at the notch chamfer.
 * The navy steel fill stays as the gradient substrate (`--steel-bg`/`-to`); the
 * mark itself renders `tone="primary"` so it contributes no competing chrome.
 *
 * CSS vars: `--steel-bg`, `--steel-bg-to`, `--steel-fg`, `--border`.
 * Design ref: design-handoff.md §5. */
export interface BrandTileProps extends Omit<AikoMarkProps, "tone" | "size"> {
  /** Tile size in px. Default 46. Drives the notched container + the mark. */
  size?: number;
}

export const BrandTile = React.forwardRef<HTMLSpanElement, BrandTileProps>(
  (
    {
      size = 46,
      animated = true,
      className,
      style,
      "aria-label": ariaLabel = "The Office — Atlas",
      ...rest
    },
    ref,
  ) => (
    <span
      ref={ref}
      role="img"
      aria-label={ariaLabel}
      className={cn(
        nx.s,
        "relative inline-flex items-center justify-center flex-shrink-0",
        "elev-card",
        className,
      )}
      style={{
        width: size,
        height: size,
        background:
          "linear-gradient(160deg, var(--steel-bg, #1B4F7E) 0%, var(--steel-bg-to, #0A2540) 100%)",
        color: "var(--steel-fg, #ffffff)",
        ...style,
      }}
      {...rest}
    >
      {/* Continuous keyline — a 1px `nx-rim` ring clipped to the `nx-s`
          silhouette, NOT a CSS border (which the notch clip-path would slice at
          the chamfer). Replaces the steel `border` from the rounded-rect era. */}
      <span
        aria-hidden
        className="nx-rim pointer-events-none absolute inset-0 [--nx-rim:hsl(var(--border))]"
        style={{ clipPath: NX_RING_CLIP.s }}
      />
      {/* The mark fills the tile; its own SVG renders at 80% so the glyph keeps
          the same inset it had in the old steel box. `bg-transparent` +
          `text-current` drop AikoMark's `primary` container fill so only the
          glyph shows over the steel substrate, inheriting the white steel ink. */}
      <AikoMark
        tone="primary"
        animated={animated}
        sizeOverride={size}
        className="absolute inset-0 bg-transparent text-current"
      />
    </span>
  ),
);
BrandTile.displayName = "BrandTile";
